/* global document, navigator */
import appConfig from 'app/config/client';
import sendPayloadToHoneybadger from 'app/services/honeybadger/honeybadger';
import * as Logger from 'app/services/logger';
import { getLocalClientRelease } from 'app/utils/clientVersion';
import { getGlobalHistory } from 'app/services/history';

const RESEND_TIMEOUT = 5000;
const SEND_REPORT_MESSAGE = 'sendErrorReport/message';
const SEND_REPORT_ERROR = 'sendErrorReport/sendError';
const NOTIFIER = {
  language: 'javascript',
  name: 'honeybadger.js',
  url: 'https://github.com/iknow',
  version: '1.0.0',
};

// The set of errors that have been passed to `sendErrorReport`, but haven't finished being sent.
// NOTE: This is an ES6 Set, not an Immutable Set.
const globalCurrentErrors = new Set();

export const reset = () => {
  globalCurrentErrors.clear();
};

/**
 * Sends an error report. Takes an error object, an optional `options` object (which
 * may contain `name` and `message` strings and a `context` object), and an optional `onError`
 * callback.
 *
 * `sendErrorReport` is wrapped with a `try/catch` that redirects all errors to the given `onError`
 * handler, so calling `sendErrorReport` should always be safe. The default `onError` handler prints
 * the error with `console.error`.
 */
export default function sendErrorReport(
  error,
  options, {
    onError = Logger.error,
    sendPayload = sendPayloadToHoneybadger,
    currentErrors = globalCurrentErrors,
  } = {},
) {
  try {
    // Don't resend an error that is currently in the process of being sent.
    if (currentErrors.has(error)) {
      return;
    }

    currentErrors.add(error);

    const payload = createPayload(error, options);
    let payloadString;

    try {
      payloadString = JSON.stringify(payload);
    } catch (serializationError) {
      // If we can't stringify the payload with the full context, try again without the context.
      payload.request.context = {
        [SEND_REPORT_MESSAGE]: 'Failed to serialize payload with context.',
        [SEND_REPORT_ERROR]: String(serializationError),
      };
      payloadString = JSON.stringify(payload);
    }

    const clearError = () => {
      currentErrors.delete(error);
    };

    sendPayload(payloadString, {
      onSuccess: clearError,
      onError: sendError => {
        // If the payload fails to send, wait a few seconds and try to send it one more time.
        setTimeout(() => {
          const safeOnError = e => {
            try {
              onError(e);
            } catch (onErrorError) {
              // If the onError callback throws an error, catch it so that it doesn't trigger a
              // global error handler and cause an infinite error-reporting loop.
            }

            clearError();
          };

          try {
            payload.request.context = {
              [SEND_REPORT_MESSAGE]: 'Resent error because it failed to send once.',
              [SEND_REPORT_ERROR]: String(sendError),
              ...payload.request.context,
            };
            payloadString = JSON.stringify(payload);
            sendPayload(payloadString, { onSuccess: clearError, onError: safeOnError });
          } catch (sendError2) {
            safeOnError(sendError2);
          }
        }, RESEND_TIMEOUT);
      },
    });
  } catch (catchAllError) {
    try {
      onError(catchAllError);
    } catch (onErrorError) {
      // Don't cause an infinite error-reporting loop.
    }
  }
}

function createPayload(err, { name, message, context } = {}) {
  const trace = generateStackTrace(err);

  return {
    error: {
      class: name || (err && err.name) || 'Error',
      message: message || (err && err.message),
      backtrace: trace.stack,
      generator: trace.generator,
    },
    request: {
      cgi_data: cgiData(),
      context: context || {},
      url: getUrl(),
    },
    server: {
      environment_name: appConfig.environment.name,
      project_root: getProjectRoot(),
      revision: getLocalClientRelease() || 'development',
    },
    notifier: NOTIFIER,
  };
}

function getUrl() {
  try {
    return document.URL;
  } catch (error) {
    return `Error getting url: ${error}`;
  }
}

function getProjectRoot() {
  try {
    const { location } = getGlobalHistory();

    return `${location.protocol}//${location.host}`;
  } catch (error) {
    return `Error getting project root: ${error}`;
  }
}

// Helper functions copied from the honeybadger-js source code.
function cgiData() {
  const data = {};
  data.HTTP_USER_AGENT = navigator.userAgent;
  if (document.referrer.match(/\S/)) {
    data.HTTP_REFERER = document.referrer;
  }
  return data;
}

function generateStackTrace(err) {
  if (err && getStackTrace(err)) {
    return { stack: getStackTrace(err), generator: undefined };
  }

  try {
    throw new Error('');
  } catch (e) {
    if (getStackTrace(e)) {
      return { stack: getStackTrace(e), generator: 'throw' };
    }
  }

  const stack = ['<call-stack>'];
  const maxStackSize = 10;
  // eslint-disable-next-line prefer-rest-params, no-caller
  let curr = arguments.callee;
  while (curr && stack.length < maxStackSize) {
    if (/function(?:\s+([\w$]+))+\s*\(/.test(curr.toString())) {
      stack.push(RegExp.$1 || '<anonymous>');
    } else {
      stack.push('<anonymous>');
    }
    try {
      curr = curr.caller;
    } catch (e) {
      break;
    }
  }

  return { stack: stack.join('\n'), generator: 'walk' };
}

function getStackTrace(err) {
  // From TraceKit: Opera 10 *destroys* its stacktrace property if you try to
  // access the stack property first.
  return err.stacktrace || err.stack || undefined;
}
